import type { ContractDetail, ContractStatus } from '@/types'

const DEFAULT_CONTRACT_VAT_RATE = 20

const EDITABLE_CONTRACT_STATUSES = [
  'draft',
  'pending_signature',
] as const satisfies readonly ContractStatus[]

export const MANUAL_CONTRACT_STATUSES = [
  'draft',
  'pending_signature',
  'signed',
  'declined',
  'active',
  'suspended',
  'expired',
  'terminated',
  'renewed',
] as const satisfies readonly ContractStatus[]

export const CONTRACT_STATUS_COLORS: Record<
  ContractStatus,
  { bg: string; text: string; dot: string }
> = {
  draft: { bg: '#f4f5f8', text: '#171a20', dot: '#303030' },
  pending_signature: { bg: '#dbeafe', text: '#1d44b3', dot: '#2f64e2' },
  signed: { bg: '#d1fae5', text: '#065f46', dot: '#24b47e' },
  declined: { bg: '#fecaca', text: '#7f1d1d', dot: '#d63b33' },
  active: { bg: '#d1fae5', text: '#065f46', dot: '#24b47e' },
  to_be_renewed: { bg: '#fef3c7', text: '#8a5405', dot: '#c77605' },
  suspended: { bg: '#ffe7ba', text: '#d46b08', dot: '#d46b08' },
  expired: { bg: '#f4f5f8', text: '#171a20', dot: '#303030' },
  terminated: { bg: '#fecaca', text: '#7f1d1d', dot: '#d63b33' },
  renewed: { bg: '#dbeafe', text: '#1d44b3', dot: '#c77605' },
}

const CONTRACT_STATUS_TRANSITIONS: Record<
  (typeof MANUAL_CONTRACT_STATUSES)[number],
  ContractStatus[]
> = {
  draft: ['pending_signature', 'signed', 'active'],
  pending_signature: ['signed', 'declined', 'active', 'draft'],
  signed: ['active', 'suspended', 'expired'],
  declined: ['draft', 'pending_signature'],
  active: ['suspended', 'terminated', 'expired'],
  suspended: ['active'],
  expired: ['renewed'],
  terminated: [],
  renewed: [],
}

export function getAllowedContractStatusOptions(
  currentStatus: ContractStatus
): ContractStatus[] {
  if (!isManualContractStatus(currentStatus)) return [currentStatus]

  return [
    currentStatus,
    ...CONTRACT_STATUS_TRANSITIONS[currentStatus].filter(
      status => status !== currentStatus && status !== 'to_be_renewed'
    ),
  ]
}

function isManualContractStatus(
  status: ContractStatus
): status is (typeof MANUAL_CONTRACT_STATUSES)[number] {
  return (MANUAL_CONTRACT_STATUSES as readonly ContractStatus[]).includes(
    status
  )
}

export function isContractEditable(status: ContractStatus): boolean {
  return (EDITABLE_CONTRACT_STATUSES as readonly ContractStatus[]).includes(
    status
  )
}

export function totalAmountInclVat(contract: ContractDetail): number | null {
  if (contract.amount == null) {
    if (!contract.lines?.length) return null
    return (
      Math.round(
        contract.lines.reduce((sum, line) => {
          const base = Number(line.line_total ?? 0)
          const tax = base * ((Number(line.tax_rate ?? 0) || 0) / 100)
          return sum + base + tax
        }, 0) * 100
      ) / 100
    )
  }
  return (
    Math.round(
      contract.amount *
        (1 + (contract.vat_rate ?? DEFAULT_CONTRACT_VAT_RATE) / 100) *
        100
    ) / 100
  )
}
